import {
  detectPushClientGate,
  permissionToToggleState,
  urlBase64ToUint8Array,
  type PushClientStatus
} from './client-support';

const SUBSCRIBE_URL = '/api/push/subscribe';
/** Which user enabled push on this device; only that user is re-registered silently. */
export const PUSH_OWNER_STORAGE_KEY = 'lqd:push-owner';

export type BrowserPushApi = {
  standalone: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  notificationPermission: NotificationPermission | 'unsupported';
  hasNotification: boolean;
  hasPushManager: boolean;
  hasServiceWorker: boolean;
  vapidPublicKey?: string;
  getRegistration: () => Promise<ServiceWorkerRegistration | undefined>;
  requestPermission: () => Promise<NotificationPermission>;
  subscribe: (
    registration: ServiceWorkerRegistration,
    applicationServerKey: Uint8Array
  ) => Promise<PushSubscription>;
  getSubscription: (
    registration: ServiceWorkerRegistration
  ) => Promise<PushSubscription | null>;
  postSubscription: (subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
  }) => Promise<boolean>;
  deleteSubscription: (endpoint: string, auth?: string) => Promise<void>;
  getServerSubscribed: (endpoint: string) => Promise<boolean>;
  /** User id that last enabled push on this device (localStorage), if any. */
  getPushOwner: () => string | null;
  setPushOwner: (userId: string | null) => void;
};

export function readBrowserPushApi(): BrowserPushApi | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator &&
      (navigator as Navigator & { standalone?: boolean }).standalone === true);
  return {
    standalone,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    notificationPermission:
      'Notification' in window ? Notification.permission : 'unsupported',
    hasNotification: 'Notification' in window,
    hasPushManager: 'PushManager' in window,
    hasServiceWorker: 'serviceWorker' in navigator,
    vapidPublicKey: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY,
    getRegistration: () => navigator.serviceWorker.getRegistration(),
    requestPermission: () => Notification.requestPermission(),
    subscribe: (registration, applicationServerKey) =>
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource
      }),
    getSubscription: registration => registration.pushManager.getSubscription(),
    postSubscription: async subscription => {
      const response = await fetch(SUBSCRIBE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription)
      });
      return response.ok;
    },
    deleteSubscription: async (endpoint, auth) => {
      await fetch(SUBSCRIBE_URL, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // `auth` lets the sign-out cleanup delete the row after Clerk has
        // already dropped the session (proof of device possession).
        body: JSON.stringify(auth ? { endpoint, auth } : { endpoint })
      });
    },
    getServerSubscribed: async endpoint => {
      const response = await fetch(
        `${SUBSCRIBE_URL}?endpoint=${encodeURIComponent(endpoint)}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        return false;
      }
      const data = (await response.json()) as { subscribed?: boolean };
      return data.subscribed === true;
    },
    getPushOwner: () => {
      try {
        return localStorage.getItem(PUSH_OWNER_STORAGE_KEY);
      } catch {
        return null;
      }
    },
    setPushOwner: userId => {
      try {
        if (userId) {
          localStorage.setItem(PUSH_OWNER_STORAGE_KEY, userId);
        } else {
          localStorage.removeItem(PUSH_OWNER_STORAGE_KEY);
        }
      } catch {
        // Private browsing / quota -- the next sign-in simply asks again.
      }
    }
  };
}

export async function readPushClientStatus(
  api: BrowserPushApi
): Promise<PushClientStatus> {
  const gate = detectPushClientGate(api);
  if (gate !== 'ready') {
    return gate;
  }
  if (!api.vapidPublicKey) {
    return 'misconfigured';
  }
  const registration = await api.getRegistration();
  if (!registration) {
    // The worker registers after mount on the first launch; the caller
    // re-syncs once `navigator.serviceWorker.ready` resolves.
    return 'pending';
  }
  if (api.notificationPermission === 'denied') {
    return 'denied';
  }
  const subscription = await api.getSubscription(registration);
  if (!subscription) {
    return 'off';
  }
  const subscribed = await api.getServerSubscribed(subscription.endpoint);
  return permissionToToggleState(api.notificationPermission, subscribed);
}

/**
 * Re-attaches an existing browser subscription to the signed-in user.
 *
 * Sign-out only deletes the server row (see {@link unsubscribePushOnSignOut}),
 * so after the next sign-in the browser still holds a granted permission and a
 * live `PushSubscription`. Posting it again lets the server upsert re-point the
 * endpoint at the current user without a permission prompt.
 *
 * Returns `null` when there is nothing to sync (no worker, no permission, no
 * subscription) so the caller keeps whatever status it already read.
 */
export async function syncExistingPushSubscription(
  api: BrowserPushApi,
  userId: string | null | undefined
): Promise<PushClientStatus | null> {
  if (detectPushClientGate(api) !== 'ready' || !api.vapidPublicKey) {
    return null;
  }
  // Consent is per user, not per device: only the person who enabled push
  // here is re-registered silently. Anyone else goes through the prompt or
  // the Settings toggle (no browser prompt, permission is already granted).
  if (!userId || api.getPushOwner() !== userId) {
    return null;
  }
  const registration = await api.getRegistration();
  if (!registration) {
    return null;
  }
  if (api.notificationPermission !== 'granted') {
    return null;
  }
  const subscription = await api.getSubscription(registration);
  if (!subscription) {
    return null;
  }
  if (await api.getServerSubscribed(subscription.endpoint)) {
    return 'on';
  }
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    return null;
  }
  const ok = await api.postSubscription({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }
  });
  return ok ? 'on' : 'off';
}

export async function enablePushNotifications(
  api: BrowserPushApi,
  userId?: string | null
): Promise<PushClientStatus> {
  const gate = detectPushClientGate(api);
  if (gate !== 'ready') {
    return gate;
  }
  if (!api.vapidPublicKey) {
    return 'misconfigured';
  }
  // Ask for permission first: iOS drops the user-gesture context across an
  // awaited registration lookup and then rejects the prompt outright.
  const permission = await api.requestPermission();
  if (permission !== 'granted') {
    return permission === 'denied' ? 'denied' : 'off';
  }
  const registration = await api.getRegistration();
  if (!registration) {
    return 'pending';
  }
  const subscription = await api.subscribe(
    registration,
    urlBase64ToUint8Array(api.vapidPublicKey)
  );
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    return 'off';
  }
  const ok = await api.postSubscription({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }
  });
  if (ok) {
    api.setPushOwner(userId ?? null);
  }
  return ok ? 'on' : 'off';
}

export async function disablePushNotifications(
  api: BrowserPushApi
): Promise<PushClientStatus> {
  const registration = await api.getRegistration();
  const subscription = registration
    ? await api.getSubscription(registration)
    : null;
  if (subscription) {
    await api.deleteSubscription(subscription.endpoint);
    await subscription.unsubscribe();
  }
  api.setPushOwner(null);
  return 'off';
}

/**
 * Sign-out cleanup: detaches this device from the user on the server only.
 *
 * The browser-side `PushSubscription` is deliberately kept so the next sign-in
 * can reuse it via {@link syncExistingPushSubscription} without prompting
 * again. Only the explicit Settings toggle ({@link disablePushNotifications})
 * unsubscribes locally. Best-effort: never throws.
 */
export async function unsubscribePushOnSignOut(
  api: Pick<
    BrowserPushApi,
    'getRegistration' | 'getSubscription' | 'deleteSubscription'
  >
): Promise<void> {
  try {
    const registration = await api.getRegistration();
    if (!registration) {
      return;
    }
    const subscription = await api.getSubscription(registration);
    if (!subscription) {
      return;
    }
    await api.deleteSubscription(
      subscription.endpoint,
      subscription.toJSON().keys?.auth
    );
  } catch {
    // Sign-out cleanup is best-effort.
  }
}
